import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Shield, Users, ArrowRight, Truck, Boxes, Ship, Plane, Clock } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const pricingPlans = [
  {
    name: 'Starter',
    description: 'Perfect for small businesses and startups',
    price: '$2,500',
    period: '/month',
    features: [
      'Up to 100 shipments/month',
      'Basic tracking & visibility',
      'Email support',
      'Standard SLAs',
      'Basic reporting',
      'Mobile app access'
    ],
    popular: false,
    icon: Truck,
    cta: 'Get Started',
    color: 'from-slate-600 to-slate-700'
  },
  {
    name: 'Professional',
    description: 'Ideal for growing companies with moderate volume',
    price: '$5,500',
    period: '/month',
    features: [
      'Up to 500 shipments/month',
      'Advanced tracking & analytics',
      'Priority support',
      'Enhanced SLAs',
      'Custom reporting',
      'API access',
      'Dedicated account manager',
      'Cross-docking services'
    ],
    popular: true,
    icon: Boxes,
    cta: 'Start Free Trial',
    color: 'from-primary-600 to-primary-700'
  },
  {
    name: 'Enterprise',
    description: 'For large organizations with complex logistics needs',
    price: 'Custom',
    period: '',
    features: [
      'Unlimited shipments',
      'Full platform access',
      '24/7 dedicated support',
      'Custom SLAs',
      'Advanced analytics & AI',
      'Full API access',
      'Dedicated team',
      'Custom integrations',
      'White-label solutions',
      'Strategic consulting'
    ],
    popular: false,
    icon: Ship,
    cta: 'Contact Sales',
    color: 'from-slate-800 to-slate-900'
  }
];

const servicePricing = [
  {
    service: 'CROSSDOCS',
    icon: Boxes,
    basePrice: '$45',
    unit: 'per pallet',
    description: 'Same-day processing with real-time visibility',
    features: ['Quality control', 'Custom labeling', 'Real-time updates', 'Insurance included']
  },
  {
    service: 'Line-Haul',
    icon: Truck,
    basePrice: '$1.25',
    unit: 'per mile',
    description: 'Regional and long-haul with strict SLAs',
    features: ['GPS tracking', 'Temperature control', 'Dedicated capacity', 'Live ETAs']
  },
  {
    service: 'Warehousing',
    icon: Shield,
    basePrice: '$0.85',
    unit: 'per sq ft/month',
    description: 'Short-term buffer storage near key hubs',
    features: ['Climate control', 'Security monitoring', 'Inventory management', 'Pick & pack']
  },
  {
    service: 'Ocean Freight',
    icon: Ship,
    basePrice: '$350',
    unit: 'per container',
    description: 'Port pickup and container deconsolidation',
    features: ['Port drayage', 'Customs clearance', 'FCL/LCL handling', 'Real-time tracking']
  },
  {
    service: 'Air Cargo',
    icon: Plane,
    basePrice: '$2.50',
    unit: 'per lb',
    description: 'Next-flight-out and time-critical shipments',
    features: ['Priority handling', 'White-glove service', 'Global reach', 'Speed guarantee']
  },
  {
    service: 'Last-Mile',
    icon: Clock,
    basePrice: '$85',
    unit: 'per delivery',
    description: 'Time-window deliveries with appointment scheduling',
    features: ['Appointment scheduling', 'Inside delivery', 'Assembly services', 'Returns handling']
  }
];

const faqs = [
  {
    question: 'How do you calculate pricing for custom solutions?',
    answer: 'Custom pricing is based on your specific requirements, volume, and service level needs. Our team will analyze your logistics operations and provide a tailored quote.'
  },
  {
    question: 'Are there any hidden fees or setup costs?',
    answer: 'No hidden fees. All costs are transparent and clearly outlined in your contract. Setup costs may apply for custom integrations or specialized equipment.'
  },
  {
    question: 'Can I change plans or services mid-contract?',
    answer: 'Yes, you can upgrade or modify your plan at any time. We offer flexible contracts that grow with your business needs.'
  },
  {
    question: 'What payment terms do you offer?',
    answer: 'We offer net-30 payment terms for established customers. New customers may require prepayment or shorter terms initially.'
  },
  {
    question: 'Do you offer volume discounts?',
    answer: 'Yes, we provide significant discounts for high-volume customers. Contact our sales team to discuss your specific volume requirements.'
  }
];

export default function Pricing() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Pricing — CROSSDOCS</title>
        <meta name="description" content="Transparent, competitive pricing for CROSSDOCS logistics services. Choose from flexible plans designed to scale with your business." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Transparent Pricing for
              <span className="block text-primary-400">Modern Logistics</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Choose the plan that fits your business needs. All plans include our core 
              logistics platform with transparent, predictable pricing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Choose Your Plan
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Start with what you need and scale as you grow. All plans include our 
              award-winning logistics platform and dedicated support.
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-3">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 ${
                  plan.popular ? 'border-primary-500 scale-105' : 'border-slate-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-primary-600 text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-8">
                    <div className={`w-16 h-16 bg-gradient-to-r ${plan.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                      <plan.icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                    <p className="text-slate-600">{plan.description}</p>
                  </div>

                  <div className="text-center mb-8">
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                      <span className="text-slate-600 ml-1">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <NavLink
                    to={plan.name === 'Enterprise' ? '/contact' : '/contact'}
                    className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      plan.popular
                        ? 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="h-4 w-4" />
                  </NavLink>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Pricing */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Service-Specific Pricing
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Transparent pricing for individual services. Mix and match based on your needs.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {servicePricing.map((service, index) => (
              <motion.div
                key={service.service}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                  <service.icon className="h-6 w-6 text-primary-600" />
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-2">{service.service}</h3>
                <p className="text-slate-600 mb-4">{service.description}</p>
                
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-2xl font-bold text-slate-900">{service.basePrice}</span>
                  <span className="text-slate-600">{service.unit}</span>
                </div>

                <ul className="space-y-2">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />
                      {feature}
                    </li>
                ))}
              </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Why Choose CROSSDOCS?
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our pricing reflects the value we deliver: faster delivery times, reduced costs, 
                and peace of mind through our comprehensive logistics platform.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: Zap,
                    title: 'Performance Guarantee',
                    description: '99.95% on-time delivery or we pay for delays'
                  },
                  {
                    icon: Shield,
                    title: 'Full Transparency',
                    description: 'Real-time tracking and complete cost visibility'
                  },
                  {
                    icon: Users,
                    title: 'Dedicated Support',
                    description: 'Personal account managers and 24/7 assistance'
                  }
                ].map((item, index) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
            </div>
          ))}
        </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Calculate Your Savings</h3>
              <p className="text-slate-600 mb-6">
                Use our ROI calculator to see how much you can save with CROSSDOCS logistics solutions.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Monthly Shipments</label>
                  <input
                    type="number"
                    placeholder="100"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Current Cost per Shipment</label>
                  <input
                    type="number"
                    placeholder="$25"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <button className="w-full bg-primary-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300">
                  Calculate Savings
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container-consistent">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Get answers to common questions about our pricing and services.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 border border-slate-200"
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </motion.div>
            ))}
      </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="container-consistent text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-primary-100 max-w-3xl mx-auto mb-8">
              Join hundreds of companies already saving money and improving their logistics 
              with CROSSDOCS. Get your free quote today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <NavLink 
                to="/contact" 
                className="inline-flex items-center rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                Get Free Quote
              </NavLink>
              <NavLink 
                to="/services" 
                className="inline-flex items-center rounded-xl px-8 py-4 text-base font-semibold text-white ring-2 ring-white/30 hover:ring-white/50 hover:bg-white/10 transition-all duration-300"
              >
                Learn More
              </NavLink>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






